import React, { useState, useEffect, useContext } from "react";
import inView from "@montana/helpers/in-view.helper";
import { CrawlerContext } from "../../context/crawler.context";
import { IImage } from "@montana/models/generated/cms.interface";


export interface IImageLoaderChildrenProps {
    loaded: boolean;
    loading: boolean;
    error: boolean;
}


export interface IImageLoaderProps {
    imageReference: IImage;
    domReference: HTMLElement | null;
    immediate?: boolean;
    noDecode?: boolean;
    ref?: any;
    onLoad?: Function;
    onLoading?: Function;
    children: (props: IImageLoaderChildrenProps) => JSX.Element | null | void;
    src?: string;
    srcSet?: string;
}


export const ImageLoader: React.FC<IImageLoaderProps> = (props: IImageLoaderProps): JSX.Element => {
    // If the "client" is actually a crawler, which is an information that's being provided to us by the
    // SSR renderer, we should just forego all the magic and show the image immediately.
    const isCrawler = useContext(CrawlerContext);

    const [loaded, setLoaded] = useState(isCrawler);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    // If the image is requested as immediate, we'll just force it to load at once.
    const [imageInView, setImageInView] = useState(Boolean(props.immediate || isCrawler));

    // Callback for the intersection observer.
    const imageMightIntersect = (intersectionData: IntersectionObserverEntry): void => {
        if (props.domReference && intersectionData.isIntersecting) {
            setImageInView(true);
        }
    };

    const [renderedOutput, setRenderedOutput] = useState<JSX.Element>(<></>);

    useEffect((): void => {

        setRenderedOutput(props.children({
            loading,
            loaded,
            error,
        }) || renderedOutput);

    }, []);

    // Set up an effect to fire when the containing reference container enters the viewport, or is
    // close enough that it won't matter. This is where we load and decode the image.
    useEffect(
        () => {
            if (imageInView && props.domReference) {
                setLoading(true);
                setLoaded(true);
                setError(false);

                inView.unobserve('lazyLoad', props.domReference);

                if (props.onLoading) {
                    props.onLoading();
                }

                if (props.onLoad) {
                    props.onLoad();
                }
            }

        },
        [imageInView],
    );


    // Set up an effect once the DOM reference is ready.
    useEffect(
        (): undefined | (() => void) => {
            if (props.domReference) {
                if (!imageInView) {
                    inView.observe(
                        'lazyLoad',
                        props.domReference,
                        imageMightIntersect,
                        { rootMargin: '200px' },
                    );

                    return (): void => inView.unobserve('lazyLoad', props.domReference as Element);
                }
            }

            return undefined;
        },
        [props.domReference],
    );

    useEffect((): void => {
        setRenderedOutput(props.children({
            loading,
            loaded,
            error,
        }) || <></>);
    }, [loading, loaded, error]);

    return renderedOutput;

};
